/* Swan Canada installation hub — accessible guide modal and FAQ behaviour. */
(function () {
  'use strict';

  const SECTION_SELECTOR = '[data-sih-section]';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class SwanInstallationHub {
    constructor(root) {
      this.root = root;
      this.modal = root.querySelector('[data-sih-modal]');
      this.dialog = root.querySelector('[data-sih-modal-dialog]');
      this.content = root.querySelector('[data-sih-modal-content]');
      this.lastFocusedElement = null;
      this.closeTimer = null;

      this.onRootClick = this.onRootClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onFaqToggle = this.onFaqToggle.bind(this);

      this.bindEvents();
    }

    bindEvents() {
      this.root.addEventListener('click', this.onRootClick);
      this.bindFaqEvents(this.root);
    }

    bindFaqEvents(scope) {
      scope?.querySelectorAll('[data-sih-faq]').forEach((details) => {
        if (details.dataset.sihFaqBound === 'true') return;
        details.dataset.sihFaqBound = 'true';
        details.addEventListener('toggle', this.onFaqToggle);
      });
    }

    unbindFaqEvents(scope) {
      scope?.querySelectorAll('[data-sih-faq]').forEach((details) => {
        details.removeEventListener('toggle', this.onFaqToggle);
        delete details.dataset.sihFaqBound;
      });
    }

    destroy() {
      this.root.removeEventListener('click', this.onRootClick);
      this.unbindFaqEvents(this.root);
      document.removeEventListener('keydown', this.onKeyDown);
      clearTimeout(this.closeTimer);
      this.closeGuide({ restoreFocus: false, immediate: true });
      delete this.root.__swanInstallationHub;
    }

    onRootClick(event) {
      const openButton = event.target.closest('[data-sih-open-guide]');
      if (openButton && this.root.contains(openButton)) {
        event.preventDefault();
        this.openGuide(openButton);
        return;
      }

      const closeButton = event.target.closest('[data-sih-modal-close]');
      if (closeButton && this.root.contains(closeButton)) {
        event.preventDefault();
        this.closeGuide();
      }
    }

    onFaqToggle(event) {
      const current = event.currentTarget;
      if (!current.open) return;

      const list = current.closest('[data-sih-faq-list]') || this.root;
      list.querySelectorAll('[data-sih-faq][open]').forEach((details) => {
        if (details !== current) details.removeAttribute('open');
      });
    }

    openGuide(button) {
      if (!this.modal || !this.dialog || !this.content) return;
      const item = button.closest('[data-sih-category-item]');
      const template = item?.querySelector('[data-sih-guide-template]');
      if (!template) return;

      clearTimeout(this.closeTimer);
      this.lastFocusedElement = button;
      this.unbindFaqEvents(this.content);
      this.content.replaceChildren(template.content.cloneNode(true));
      this.bindFaqEvents(this.content);
      this.modal.hidden = false;
      document.documentElement.classList.add('sih-modal-open');
      document.addEventListener('keydown', this.onKeyDown);

      window.requestAnimationFrame(() => {
        this.modal.classList.add('is-open');
        const closeButton = this.modal.querySelector('.sih-modal__close');
        (closeButton || this.dialog).focus({ preventScroll: true });
      });
    }

    closeGuide(options) {
      if (!this.modal || this.modal.hidden) return;
      const settings = Object.assign({ restoreFocus: true, immediate: false }, options || {});
      this.modal.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKeyDown);
      document.documentElement.classList.remove('sih-modal-open');

      const finishClose = () => {
        this.modal.hidden = true;
        this.unbindFaqEvents(this.content);
        this.content?.replaceChildren();
        if (settings.restoreFocus && this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
          this.lastFocusedElement.focus({ preventScroll: true });
        }
        this.lastFocusedElement = null;
      };

      if (settings.immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finishClose();
      } else {
        clearTimeout(this.closeTimer);
        this.closeTimer = window.setTimeout(finishClose, 190);
      }
    }

    onKeyDown(event) {
      if (!this.modal || this.modal.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeGuide();
        return;
      }

      if (event.key !== 'Tab' || !this.dialog) return;
      const focusable = Array.from(this.dialog.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);

      if (!focusable.length) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    openGuideByKey(key) {
      const normalize = (value) => String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const requested = normalize(key);
      if (!requested) return false;

      const item = Array.from(this.root.querySelectorAll('[data-sih-category-item]')).find((candidate) => {
        const keys = [
          candidate.dataset.sihGuideKey,
          candidate.dataset.sihGuideTitleKey,
          candidate.querySelector('.sih-category-card__body strong')?.textContent
        ].map(normalize).filter(Boolean);
        return keys.includes(requested);
      });

      const button = item?.querySelector('[data-sih-open-guide]');
      if (!button) return false;
      this.openGuide(button);
      return true;
    }

    openBlock(blockId) {
      if (!blockId) return;
      const escape = window.CSS && typeof window.CSS.escape === 'function'
        ? window.CSS.escape(blockId)
        : String(blockId).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
      const button = this.root.querySelector(`[data-block-id="${escape}"] [data-sih-open-guide]`);
      if (button) this.openGuide(button);
    }
  }

  const initSection = (root) => {
    if (!root || root.__swanInstallationHub) return;
    const instance = new SwanInstallationHub(root);
    root.__swanInstallationHub = instance;

    if (document.documentElement.dataset.sihInitialGuideOpened !== 'true') {
      const guideKey = new URLSearchParams(window.location.search).get('guide');
      if (guideKey) {
        window.requestAnimationFrame(() => {
          if (instance.openGuideByKey(guideKey)) {
            document.documentElement.dataset.sihInitialGuideOpened = 'true';
          }
        });
      }
    }
  };

  const initAll = (scope) => {
    const context = scope || document;
    if (context.matches?.(SECTION_SELECTOR)) initSection(context);
    context.querySelectorAll?.(SECTION_SELECTOR).forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(SECTION_SELECTOR)
      ? event.target
      : event.target.querySelector?.(SECTION_SELECTOR);
    root?.__swanInstallationHub?.destroy();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.matches?.(SECTION_SELECTOR)
      ? event.target
      : event.target.closest?.(SECTION_SELECTOR) || event.target.querySelector?.(SECTION_SELECTOR);
    root?.__swanInstallationHub?.openBlock(event.detail?.blockId);
  });
})();
